import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  CircularProgress,
  TextField,
  InputAdornment,
} from '@mui/material';
import {
  Psychology as LLMIcon,
  MenuBook as KnowledgeIcon,
  Build as ToolsIcon,
  TextSnippet as PromptsIcon,
  SmartToy as AgentIcon,
  Search as SearchIcon,
  Code as ParserIcon,
} from '@mui/icons-material';
import { KnowledgeBase } from '../../../../Shared-ui/Types/KnowledgeBase';
import Tool from '../../../../Shared-ui/Types/Tool';
import Template from '../../../../Shared-ui/Types/Template';
import BaseInstance from '../../../../Services/AxiosInstance/BaseInstance';
import { KNOWLEDGE_BASE, TOOLS, MODELS } from '../../../../Services/Endpoint';

interface NodeTemplate {
  id: string;
  label: string;
  description: string;
  category: 'LLM Models' | 'Knowledge Bases' | 'Tools' | 'Prompts' | 'Agent' | 'Parser';
  icon: React.ReactNode;
  knowledgeBase?: KnowledgeBase;
  tool?: Tool;
  template?: Template;
  provider?: string;
  models?: string[];
}

const nodeTemplates: NodeTemplate[] = [
  // LLM Models will be loaded dynamically from API

  // Agent
  {
    id: 'agent',
    label: 'Agent',
    description: 'AI Agent node',
    category: 'Agent',
    icon: <AgentIcon />,
  },

  // Parser
  {
    id: 'parser',
    label: 'Parser',
    description: 'Parse and extract structured data from content',
    category: 'Parser',
    icon: <ParserIcon />,
  },
];

const getCategoryIcon = (category: string) => {
  switch (category) {
    case 'LLM Models':
      return <LLMIcon />;
    case 'Knowledge Bases':
      return <KnowledgeIcon />;
    case 'Tools':
      return <ToolsIcon />;
    case 'Prompts':
      return <PromptsIcon />;
    case 'Agent':
      return <AgentIcon />;
    case 'Parser':
      return <ParserIcon />;
    default:
      return null;
  }
};

const getCategoryColor = (category: string) => {
  return '#6B7280'; // Light blue-gray for all categories
};

interface NodePaletteProps {
  onNodeDragStart?: (event: React.DragEvent, nodeType: string) => void;
}

const NodePalette: React.FC<NodePaletteProps> = ({ onNodeDragStart }) => {
  const [tools, setTools] = useState<Tool[]>([]);
  const [loadingTools, setLoadingTools] = useState(false);
  const [providers, setProviders] = useState<Record<string, string[]>>({});
  const [loadingProviders, setLoadingProviders] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Fetch providers on component mount
  useEffect(() => {
    const fetchProviders = async () => {
      try {
        setLoadingProviders(true);
        const response = await BaseInstance.get(MODELS);
        setProviders(response.data);
      } catch (error) {
        console.error('Failed to fetch providers:', error);
        // Fallback to hardcoded data for testing
        const fallbackProviders = {
          "OpenAI": ["gpt-4o", "gpt-4o-mini", "gpt-4", "gpt-3.5-turbo"],
          "Anthropic": ["claude-3-opus", "claude-3-sonnet", "claude-3-haiku"],
          "Google": ["gemini-1.5-pro", "gemini-1.5-flash", "gemini-1.0-pro"]
        };
        setProviders(fallbackProviders);
      } finally {
        setLoadingProviders(false);
      }
    };
    fetchProviders();
  }, []);


  // Fetch tools on component mount
  useEffect(() => {
    const fetchTools = async () => {
      try {
        setLoadingTools(true);
        const response = await BaseInstance.get(`${TOOLS}?page=1&page_size=100`);
        setTools(response.data.items || []);
      } catch (error) {
        console.error('Failed to fetch tools:', error);
        setTools([]);
      } finally {
        setLoadingTools(false);
      }
    };

    fetchTools();
  }, []);

  // Create dynamic provider templates (LLM Models)
  const providerTemplates: NodeTemplate[] = Object.entries(providers).map(([provider, models]) => ({
    id: `provider-${provider.toLowerCase()}`,
    label: provider === 'OpenAI' ? 'Models' : provider,
    description: `${provider} models`,
    category: 'LLM Models' as const,
    icon: <LLMIcon />,
    provider: provider,
    models: Array.isArray(models) ? models : [],
  }));

  // Create single knowledge base template
  const knowledgeBaseTemplates: NodeTemplate[] = [{
    id: 'knowledge-base',
    label: 'Knowledge Base',
    description: 'Select a knowledge base to use',
    category: 'Knowledge Bases' as const,
    icon: <KnowledgeIcon />,
  }];

  // Create dynamic tool templates
  const toolTemplates: NodeTemplate[] = tools.map((tool) => ({
    id: `tool-${tool.id}`,
    label: tool.name,
    description: tool.description || `${tool.type} tool`,
    category: 'Tools' as const,
    icon: <ToolsIcon />,
    tool: tool,
  }));

  // Create single system prompt template
  const promptTemplates: NodeTemplate[] = [{
    id: 'custom-prompt', // Changed from 'system-prompt' to 'custom-prompt'
    label: 'System Prompt',
    description: 'Enter your system prompt or select from templates',
    category: 'Prompts' as const,
    icon: <PromptsIcon />,
    template: {
      id: 'custom-prompt', // Changed from 'system-prompt' to 'custom-prompt'
      name: 'System Prompt',
      description: 'Enter your system prompt or select from templates',
      category: 'custom',
      icon: null, // Remove React component to avoid circular reference
      tags: ['system', 'prompt'],
      popularity: 0,
      content: '',
    },
  }];

  // Combine static templates with dynamic knowledge bases, tools, and templates
  const allNodeTemplates = [
    ...nodeTemplates,
    ...providerTemplates,
    ...knowledgeBaseTemplates,
    ...toolTemplates,
    ...promptTemplates, // Only system prompt
  ];

  const categories = ['Agent', 'LLM Models', 'Knowledge Bases', 'Tools', 'Prompts', 'Parser'] as const;

  // Filter nodes based on search term
  const filteredNodeTemplates = allNodeTemplates.filter(node =>
    node.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
    node.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleDragStart = (event: React.DragEvent, nodeTemplate: NodeTemplate) => {
    // Create a serializable version without React components
    const serializableTemplate = {
      id: nodeTemplate.id,
      label: nodeTemplate.label,
      description: nodeTemplate.description,
      category: nodeTemplate.category,
      knowledgeBase: nodeTemplate.knowledgeBase,
      tool: nodeTemplate.tool,
      provider: nodeTemplate.provider,
      models: nodeTemplate.models,
      template: nodeTemplate.template ? {
        id: nodeTemplate.template.id,
        name: nodeTemplate.template.name,
        description: nodeTemplate.template.description,
        category: nodeTemplate.template.category,
        tags: nodeTemplate.template.tags,
        popularity: nodeTemplate.template.popularity,
        content: nodeTemplate.template.content,
        // Exclude icon to avoid circular reference
      } : undefined,
    };

    event.dataTransfer.setData('application/reactflow', JSON.stringify(serializableTemplate));
    event.dataTransfer.effectAllowed = 'move';
    if (onNodeDragStart) {
      onNodeDragStart(event, nodeTemplate.id);
    }
  };

  return (
    <Paper
      elevation={2}
      sx={{
        width: 280,
        height: '100%',
        borderRadius: 0,
        borderRight: 1,
        borderColor: 'divider',
        overflow: 'hidden',
        backgroundColor: '#ffffff',
      }}
    >
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search nodes..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 1,
            },
          }}
        />
      </Box>

      <Box sx={{ overflow: 'auto', height: 'calc(100% - 80px)', p: 1.5 }}>
        {categories.map((category) => {
          const categoryNodes = filteredNodeTemplates.filter(node => node.category === category);
          const categoryColor = getCategoryColor(category);

          // Don't show category if no nodes match the search
          if (categoryNodes.length === 0) return null;

          return (
            <Box key={category} sx={{ mb: 2.5 }}>
              {/* Category Title */}
              <Typography
                variant="caption"
                sx={{
                  color: '#9CA3AF',
                  fontWeight: 500,
                  fontSize: '11px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  mb: 1.5,
                  px: 1,
                  display: 'block'
                }}
              >
                {category}
              </Typography>

              {/* Category Items */}
              <List dense sx={{ py: 0 }}>
                {categoryNodes.map((node) => (
                  <ListItem key={node.id} disablePadding sx={{ mb: 0.5 }}>
                    <ListItemButton
                      draggable
                      onDragStart={(e) => handleDragStart(e, node)}
                      sx={{
                        py: 0.75,
                        px: 1,
                        borderRadius: 0.75,
                        minHeight: 40,
                        '&:hover': {
                          backgroundColor: '#F9FAFB',
                        },
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          minWidth: 28,
                          mr: 1.5,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 20,
                          height: 20,
                          borderRadius: 0.5,
                          backgroundColor: '#F3F4F6',
                          color: categoryColor,
                        }}
                      >
                        {node.icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 400,
                              color: '#374151',
                              fontSize: '13px',
                              lineHeight: 1.2,
                            }}
                          >
                            {node.label}
                          </Typography>
                        }
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </Box>
          );
        })}
      </Box>
    </Paper>
  );
};

export default NodePalette;
